// CadernoPopupPage — página dedicada renderizada quando o aluno abre o caderno
// em janela à parte (window.open de /student/caderno-popup/<sessionId>).
// Busca a sessão da API e renderiza só o EssayCadernoPanel em modo "popout".
//
// O conteúdo é "frozen" no momento que abrir: pra atualizar (ex: aluno mudou
// fragmentos via outra aba), refresca a janela popup manualmente. Pra MVP é
// suficiente — fragmentos não mudam durante a escrita.
//
// [[project-caderno-realista]]

import { useEffect, useState } from "preact/hooks";
import { Loader2 } from "lucide-react";
import { api, type AiStudySessionDetail } from "./api";
import { EssayCadernoPanel } from "./EssayCadernoPanel";

interface State {
  phase: "loading" | "ready" | "error";
  data: AiStudySessionDetail | null;
  err: string | null;
}

export function CadernoPopupPage() {
  const [state, setState] = useState<State>({ phase: "loading", data: null, err: null });

  // Extrai sessionId do path: /student/caderno-popup/<sessionId>
  const sessionId = (() => {
    const m = window.location.pathname.match(
      /\/student\/caderno-popup\/([^/]+)/
    );
    return m ? decodeURIComponent(m[1]) : null;
  })();

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null, err: "Sessão não encontrada na URL" });
      return;
    }
    document.title = "Caderno — Redação";
    void (async () => {
      try {
        const d = await api.getAiStudy(sessionId);
        setState({ phase: "ready", data: d, err: null });
      } catch (e) {
        setState({
          phase: "error",
          data: null,
          err: e instanceof Error ? e.message : String(e)
        });
      }
    })();
  }, [sessionId]);

  if (state.phase === "loading") {
    return (
      <div class="flex h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span class="ml-2 text-sm">Abrindo caderno…</span>
      </div>
    );
  }

  if (state.phase === "error" || !state.data) {
    return (
      <div class="flex h-screen items-center justify-center p-6 text-center text-sm text-destructive">
        {state.err ?? "Erro ao carregar a sessão."}
      </div>
    );
  }

  const { data } = state;
  const meta = data.session.essayMeta ?? null;
  const fragments = data.fragments ?? [];

  return (
    <EssayCadernoPanel
      sessionId={data.session.id}
      meta={meta}
      fragments={fragments}
      title={data.session.title || data.session.theme || "Caderno"}
      viewMode="popout"
      isPopoutWindow={true}
    />
  );
}
